import { Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, firstValueFrom } from 'rxjs';
import { ERROR_URL } from 'src/app/app/app.constants';
import { isInMenu, isMenuAllowed } from 'src/app/common/menu-def.provider';
import { DemoService } from 'src/app/services/demo/demo.service';
import { shouldBypassBenchmarksReportAccess } from 'src/app/services/demo/demo-mock-data-visibility';
import { AuthState } from 'src/app/store/auth/auth.state';

@Injectable({
  providedIn: 'root',
})
export class MenuGuard implements CanActivate, CanActivateChild {
  constructor(
    private store: Store,
    private router: Router,
    private injector: Injector,
    private readonly demoService: DemoService,
    @Inject(ERROR_URL) private errorUrl,
  ) { }

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const urlTree = this.router.parseUrl(state.url);
    const urlWithoutParams = urlTree.root.children?.primary?.segments?.map(it => it.path).join('/') || '';
    const company = this.store.selectSnapshot(AuthState.company);
    const isManager = this.store.selectSnapshot(AuthState.isManager);
    let error = 'unauthorized';

    if (urlWithoutParams === 'benchmarks' && await this.isBenchmarksMockDataAccessAllowed()) {
      return true;
    }

    if (!isMenuAllowed(company, this.injector, '/' + urlWithoutParams)) {
      if (state.url === '/' && company.role === 'admin' && company?.userSettings?.disableReportsAccess) {
        return this.router.createUrlTree(['manage-users']);
      }
      if (urlWithoutParams && isInMenu('/' + urlWithoutParams)) {
        if (isManager && this.store.selectSnapshot(AuthState.webAppTrackingMode) === 'off') {
          if (state.url === '/productivity-ratings') {
            error = 'productivityRatings';
          }
          if (state.url === '/web-app-usage') {
            error = 'webAppUsage';
          }
        }
        if (isManager && state.url === '/notifications' && company.pricingPlan?.includes('basic')) {
          error = 'notifications';
        }
        return this.router.createUrlTree([this.errorUrl, error]);
      }
    }
    return true;
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  private async isBenchmarksMockDataAccessAllowed(): Promise<boolean> {
    const showBenchmarksDemoData = await firstValueFrom(
      this.demoService.showBenchmarksDemoData$.pipe(filter((value): value is boolean => value !== undefined)),
    );
    return shouldBypassBenchmarksReportAccess(showBenchmarksDemoData);
  }

}
